/**
 * NavigationSkeletonOutlet — RR7 counterpart of the legacy `renderLoading` / `keepMounted` statics.
 *
 * Legacy `@s-ui/react-initial-props` mounted the next page at once and rendered its
 * `Page.renderLoading()` while `getInitialProps` ran on the client, so going from the listing
 * to an ad showed the detail skeleton straight away. RR7 loaders block instead: the previous
 * page stays on screen, with no feedback, until the loader resolves.
 *
 * This renders, in place of <Outlet />, the skeleton declared in the destination route's
 * `handle.navigationSkeleton` while a navigation is loading. Like `keepMounted`, a page that
 * keeps itself mounted (the listing when filtering) gets no skeleton when the destination is
 * the same page.
 *
 * SSR and hydration always see `navigation.state === 'idle'`, so the first render is <Outlet />.
 */
import type {ComponentType} from 'react'
import type {Location, Params, RouteObject} from 'react-router-dom'
import {matchRoutes, Outlet, useLocation, useNavigation} from 'react-router-dom'

export interface NavigationSkeleton {
  Skeleton: ComponentType
  keepMounted?: boolean
}

// A function when one route serves more than one page (the `*` catch-all: listing or ad detail).
export type NavigationSkeletonHandle =
  | NavigationSkeleton
  | ((match: {pathname: string; params: Params}) => NavigationSkeleton | undefined)

function getNavigationSkeleton(routes: RouteObject[], location: Location): NavigationSkeleton | undefined {
  const matches = matchRoutes(routes, location) ?? []
  const match = matches[matches.length - 1]
  const handle: NavigationSkeletonHandle | undefined = match?.route.handle?.navigationSkeleton
  if (match == null || handle == null) return undefined
  return typeof handle === 'function' ? handle({pathname: location.pathname, params: match.params}) : handle
}

export function NavigationSkeletonOutlet({routes}: {routes: RouteObject[]}) {
  const navigation = useNavigation()
  const location = useLocation()
  const nextLocation = navigation.location

  if (navigation.state !== 'loading' || nextLocation == null) return <Outlet />
  if (nextLocation.pathname === location.pathname && nextLocation.search === location.search) return <Outlet />

  const nextSkeleton = getNavigationSkeleton(routes, nextLocation)
  if (nextSkeleton == null) return <Outlet />

  const isSamePageKeptMounted =
    nextSkeleton.keepMounted === true && getNavigationSkeleton(routes, location)?.Skeleton === nextSkeleton.Skeleton
  if (isSamePageKeptMounted) return <Outlet />

  const {Skeleton} = nextSkeleton
  return <Skeleton />
}
